import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, ArrowRight, Flame } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Inline firm acquisition CTA for SEO landing pages.
 *
 * Captures any inbound law-firm visitor (typically arriving from Google for
 * "[state] [practice] attorney" queries) into the OUTREACH pipeline with a
 * one-form, no-card-required offer:
 *   - 5 free pre-analysed leads
 *   - Auto-generated access code
 *   - Welcome email if Resend is configured
 *
 * The form posts to /api/firms/inbound-claim which:
 *   1. Creates a `law_firms` row with trial=5
 *   2. Logs an `outreach_packets` entry with status='inbound' so the operator
 *      can immediately see real inbound conversions in the OUTREACH tab.
 */
export default function FirmInboundCTA({ state = "", practice = "", sourceSlug = "" }) {
    const [email, setEmail] = useState("");
    const [firmName, setFirmName] = useState("");
    const [contactName, setContactName] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);
    const [stateStats, setStateStats] = useState(null);

    useEffect(() => {
        if (!state) return;
        axios
            .get(`${API}/firms-state-count`, { params: { state } })
            .then((r) => setStateStats(r.data))
            .catch(() => setStateStats(null));
    }, [state]);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!email.includes("@")) {
            toast.error("Enter a valid work email");
            return;
        }
        setBusy(true);
        try {
            const res = await axios.post(`${API}/firms/inbound-claim`, {
                contact_email: email,
                firm_name: firmName,
                contact_name: contactName,
                state,
                practice,
                source_slug: sourceSlug,
            });
            setDone(res.data);
            toast.success("5 free leads unlocked. Check your inbox.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not unlock leads. Try again.");
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div
                className="border-2 border-[#00FF85] bg-[#00FF85]/5 p-6"
                data-testid="firm-inbound-success"
            >
                <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 size={20} className="text-[#00FF85]" />
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FF85]">
                        5 FREE LEADS UNLOCKED
                    </div>
                </div>
                <div className="font-anton text-2xl uppercase mb-2">
                    Welcome to HPM, {state ? state : "Counselor"}.
                </div>
                <p className="font-sans text-sm text-white/80 leading-relaxed mb-3">
                    Your access code: <span className="font-mono text-white">{done.access_code}</span>
                </p>
                <a
                    href={done.claim_url}
                    className="btn-brutal btn-brutal-red inline-flex text-[10px]"
                    data-testid="firm-inbound-claim-link"
                >
                    CLAIM MY 5 LEADS NOW <ArrowRight size={12} />
                </a>
            </div>
        );
    }

    return (
        <form
            onSubmit={submit}
            className="border-2 border-[#FF3B30] bg-[#FF3B30]/5 p-6"
            data-testid="firm-inbound-cta"
        >
            {stateStats && state && (
                <div
                    className="mb-4 -mt-1 border border-[#FFCC00]/50 bg-[#FFCC00]/5 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFCC00] flex items-center gap-2"
                    data-testid="firm-state-scarcity"
                >
                    <Flame size={11} />
                    {stateStats.is_empty ? (
                        <span>
                            BE THE FIRST FIRM IN {state.toUpperCase()} · CLAIM SPOT #1
                        </span>
                    ) : (
                        <span>
                            {stateStats.active_firms} {stateStats.active_firms === 1 ? "FIRM" : "FIRMS"} IN {state.toUpperCase()} · YOU&apos;LL BE #{stateStats.next_position}
                        </span>
                    )}
                </div>
            )}
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-2">
                FOR FIRMS · NO CARD · NO COMMITMENT
            </div>
            <div className="font-anton text-2xl sm:text-3xl uppercase leading-none mb-2">
                Get 5 free {state ? state.toUpperCase() : ""} {practice ? practice.toUpperCase() : ""} leads.
            </div>
            <p className="font-sans text-sm text-white/70 mb-4 leading-relaxed">
                Pre-analysed by 10 HPM AI bots. Strength score · violations · evidence gaps.
                One inbox, zero hunting.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <input
                    type="email"
                    required
                    placeholder="WORK EMAIL"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-black border-2 border-white/30 p-3 font-mono text-xs text-white placeholder-white/40 focus:border-[#FF3B30] outline-none"
                    data-testid="firm-inbound-email"
                />
                <input
                    type="text"
                    placeholder="FIRM NAME (OPTIONAL)"
                    value={firmName}
                    onChange={(e) => setFirmName(e.target.value)}
                    className="bg-black border-2 border-white/30 p-3 font-mono text-xs text-white placeholder-white/40 focus:border-[#FF3B30] outline-none"
                    data-testid="firm-inbound-firm-name"
                />
                <input
                    type="text"
                    placeholder="YOUR NAME (OPTIONAL)"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    className="bg-black border-2 border-white/30 p-3 font-mono text-xs text-white placeholder-white/40 focus:border-[#FF3B30] outline-none col-span-1 sm:col-span-2"
                    data-testid="firm-inbound-contact-name"
                />
            </div>
            <button
                type="submit"
                disabled={busy}
                className="btn-brutal btn-brutal-red text-[10px] w-full justify-center"
                data-testid="firm-inbound-submit"
            >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <>UNLOCK 5 FREE LEADS <ArrowRight size={12} /></>}
            </button>
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 mt-2 text-center">
                ZERO RISK · NO CARD · WORKS FOREVER
            </p>
        </form>
    );
}
